import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Mail, Phone, Search, Trash2, RefreshCw, LogOut, Inbox, CalendarClock,
  CheckCircle2, AlertCircle, Loader2, ExternalLink, ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/site/SEO";
import BrandLogo from "@/components/site/BrandLogo";

function fmt(dt) {
  try {
    return new Date(dt).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch { return String(dt); }
}

export default function AdminDashboard() {
  const { user, logout, api } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, last_7d: 0, emailed: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/admin/contact-submissions"),
        api.get("/admin/stats"),
      ]);
      setRows(list);
      setStats(s);
      if (list.length && !active) setActive(list[0]);
    } catch (e) {
      toast.error("Could not load submissions");
    } finally {
      setLoading(false);
    }
  }, [api, active]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/contact-submissions/${id}`);
      toast.success("Submission deleted");
      setRows((r) => r.filter((x) => x.id !== id));
      if (active?.id === id) setActive(null);
    } catch {
      toast.error("Could not delete");
    }
  };

  const filtered = rows.filter((r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [r.full_name, r.email, r.telephone, r.subject, r.message]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink" data-testid="admin-dashboard-page">
      <SEO title="Admin Dashboard" path="/admin" />

      {/* TOP BAR */}
      <header className="bg-white border-b border-brand-line sticky top-0 z-30">
        <div className="container-page flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <BrandLogo variant="wordmark" className="h-8 w-auto" />
            <div className="leading-tight">
              <div className="font-display font-extrabold">
                OneDial <span className="text-brand-green">Admin</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">
                Contact Submissions
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/v1"
              data-testid="admin-open-v1"
              className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover px-4 py-2 text-xs font-semibold text-white shadow-sm"
            >
              Admin V1 (Users · Wallets · Payments)
            </Link>
            <Link
              to="/"
              data-testid="admin-back-to-site"
              className="hidden sm:inline-flex items-center gap-2 text-xs font-semibold text-brand-ink/80 hover:text-brand-ink"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to site
            </Link>
            <span className="hidden md:block text-xs text-brand-muted">
              Signed in as <strong className="text-brand-ink">{user?.email}</strong>
            </span>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-md border border-brand-line bg-white px-3 py-1.5 text-xs font-semibold text-brand-ink hover:bg-brand-bg"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-page py-8">
        {/* STATS */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="admin-stats">
          <StatCard icon={Inbox} label="Total submissions" value={stats.total} accent="green" />
          <StatCard icon={CalendarClock} label="Last 7 days" value={stats.last_7d} accent="ink" />
          <StatCard icon={CheckCircle2} label="Email notifications sent" value={stats.emailed} accent="gold" />
        </div>

        {/* TOOLBAR */}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, phone, subject…"
              data-testid="admin-search"
              className="w-full rounded-md border border-brand-line bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
            />
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 rounded-md border border-brand-line bg-white px-4 py-2.5 text-sm font-semibold text-brand-ink hover:bg-brand-bg"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* LIST + DETAIL */}
        <div className="mt-6 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div className="rounded-xl border border-brand-line bg-white overflow-hidden" data-testid="admin-submissions-list">
              {loading ? (
                <div className="p-10 flex items-center justify-center text-brand-muted">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center">
                  <AlertCircle className="h-6 w-6 mx-auto text-brand-muted" />
                  <p className="mt-3 text-sm text-brand-muted">No submissions match your search.</p>
                </div>
              ) : (
                <ul className="divide-y divide-brand-line">
                  {filtered.map((r) => {
                    const isActive = active?.id === r.id;
                    return (
                      <li
                        key={r.id}
                        data-testid={`admin-row-${r.id}`}
                        onClick={() => setActive(r)}
                        className={`px-5 py-4 cursor-pointer transition-colors ${
                          isActive ? "bg-brand-bg" : "hover:bg-brand-bg/60"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <p className="font-semibold truncate">{r.full_name}</p>
                            <p className="text-xs text-brand-muted truncate">{r.subject}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-brand-muted">{fmt(r.created_at)}</p>
                            <p className={`mt-1 text-[10px] uppercase tracking-[0.18em] font-semibold ${r.email_sent ? "text-brand-green" : "text-brand-muted"}`}>
                              {r.email_sent ? "Emailed" : "Stored"}
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            {active ? (
              <div className="rounded-xl border border-brand-line bg-white p-6 lg:p-7" data-testid="admin-submission-detail">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="label-eyebrow">Submission</p>
                    <h3 className="mt-2 font-display font-bold text-xl text-brand-ink">{active.subject}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDelete(active.id)}
                    data-testid="admin-delete"
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>

                <dl className="mt-5 space-y-3 text-sm">
                  <Row label="From">{active.full_name}</Row>
                  <Row label="Email">
                    <a href={`mailto:${active.email}`} className="text-brand-green hover:underline inline-flex items-center gap-1">
                      {active.email} <ExternalLink className="h-3 w-3" />
                    </a>
                  </Row>
                  <Row label="Telephone">
                    <a href={`tel:${active.telephone}`} className="text-brand-green hover:underline">{active.telephone}</a>
                  </Row>
                  <Row label="Received">{fmt(active.created_at)}</Row>
                  <Row label="Notification">
                    {active.email_sent ? (
                      <span className="inline-flex items-center gap-1 text-brand-green">
                        <CheckCircle2 className="h-4 w-4" /> Sent via {active.email_provider || "email"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-brand-muted">
                        <Inbox className="h-4 w-4" /> Stored (email provider not configured)
                      </span>
                    )}
                  </Row>
                </dl>

                <div className="mt-6 border-t border-brand-line pt-5">
                  <p className="label-eyebrow">Message</p>
                  <p className="mt-3 text-sm text-brand-ink leading-relaxed whitespace-pre-wrap">{active.message}</p>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  <a
                    href={`mailto:${active.email}?subject=Re:%20${encodeURIComponent(active.subject)}`}
                    className="inline-flex items-center gap-2 rounded-md bg-brand-green px-4 py-2 text-xs font-semibold text-white hover:bg-brand-greenHover"
                    data-testid="admin-reply-email"
                  >
                    <Mail className="h-3.5 w-3.5" /> Reply via email
                  </a>
                  <a
                    href={`tel:${active.telephone}`}
                    className="inline-flex items-center gap-2 rounded-md border border-brand-line bg-white px-4 py-2 text-xs font-semibold text-brand-ink hover:bg-brand-bg"
                  >
                    <Phone className="h-3.5 w-3.5" /> Call
                  </a>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-brand-line bg-white/60 p-10 text-center text-sm text-brand-muted">
                Select a submission to view details.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  const accentClasses = {
    green: "text-brand-green bg-brand-green/10",
    ink: "text-brand-ink bg-brand-ink/5",
    gold: "text-brand-gold bg-brand-gold/15",
  }[accent] || "text-brand-ink bg-brand-bg";
  return (
    <div className="rounded-xl border border-brand-line bg-white p-5 flex items-center gap-4">
      <div className={`h-12 w-12 rounded-md flex items-center justify-center ${accentClasses}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-display font-black text-2xl text-brand-ink">{value}</div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-muted">{label}</div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <dt className="text-[11px] uppercase tracking-[0.22em] text-brand-muted">{label}</dt>
      <dd className="col-span-2 break-words">{children}</dd>
    </div>
  );
}
